import axiosInstance from './axios';

// Admin client for the Custom Cakes (cc_*) option model (task-191). Mirrors
// backend/app/api/admin_cc_options.py and backend/app/schemas/cc_options.py.
// Group/item updates echo back the `updated_at` read from the last GET so the
// backend can reject a stale save with 409 (optimistic concurrency).

const BASE = '/admin/cc-options';

export type CakeOptionSelectionMode = 'single' | 'multiple';

export interface CakeOptionItem {
  id: number;
  group_id: number;
  name: string;
  name_zh?: string | null;
  default_price: number;
  sort_order: number;
  active: boolean;
  updated_at: string | null;
}

export interface CakeOptionGroup {
  id: number;
  name: string;
  name_zh?: string | null;
  selection_mode: CakeOptionSelectionMode;
  required: boolean;
  sort_order: number;
  active: boolean;
  // Linked cake categories (category_id). Sub-categories inherit these.
  category_ids: number[];
  items: CakeOptionItem[];
  updated_at: string | null;
}

export interface CakeOptionGroupInput {
  name: string;
  name_zh?: string | null;
  selection_mode: CakeOptionSelectionMode;
  required: boolean;
  sort_order: number;
  active: boolean;
  category_ids: number[];
  updated_at?: string | null;
}

export interface CakeOptionItemInput {
  name: string;
  name_zh?: string | null;
  default_price: number;
  sort_order: number;
  active: boolean;
  updated_at?: string | null;
}

export const adminCakeOptionGroups = {
  getAll: async (): Promise<CakeOptionGroup[]> => {
    const response = await axiosInstance.get<CakeOptionGroup[]>(`${BASE}/groups`);
    return response.data;
  },

  create: async (data: CakeOptionGroupInput): Promise<CakeOptionGroup> => {
    const response = await axiosInstance.post<CakeOptionGroup>(`${BASE}/groups`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<CakeOptionGroupInput>): Promise<CakeOptionGroup> => {
    const response = await axiosInstance.put<CakeOptionGroup>(`${BASE}/groups/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/groups/${id}`);
  },
};

export const adminCakeOptionItems = {
  create: async (groupId: number, data: CakeOptionItemInput): Promise<CakeOptionItem> => {
    const response = await axiosInstance.post<CakeOptionItem>(`${BASE}/groups/${groupId}/items`, data);
    return response.data;
  },

  update: async (id: number, data: Partial<CakeOptionItemInput>): Promise<CakeOptionItem> => {
    const response = await axiosInstance.put<CakeOptionItem>(`${BASE}/items/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${BASE}/items/${id}`);
  },

  toggle: async (id: number): Promise<CakeOptionItem> => {
    const response = await axiosInstance.patch<CakeOptionItem>(`${BASE}/items/${id}/toggle`);
    return response.data;
  },
};

// Per-product overrides (GET/PUT /admin/cc-options/products/{id}). Row absence
// = enabled at the default price; price_override null = use the default price.
export interface ProductOverrideItemState {
  id: number;
  name: string;
  name_zh?: string | null;
  default_price: number;
  sort_order: number;
  active: boolean;
  disabled: boolean;
  price_override: number | null;
  effective_price: number;
}

export interface ProductOverrideGroupState {
  id: number;
  name: string;
  name_zh?: string | null;
  selection_mode: CakeOptionSelectionMode;
  required: boolean;
  sort_order: number;
  items: ProductOverrideItemState[];
}

export interface ProductOverridesState {
  product_id: number;
  groups: ProductOverrideGroupState[];
}

export interface ProductOverrideInput {
  option_item_id: number;
  disabled: boolean;
  price_override: number | null;
}

export const adminCakeOptionProductOverrides = {
  get: async (productId: string | number): Promise<ProductOverridesState> => {
    const response = await axiosInstance.get<ProductOverridesState>(`${BASE}/products/${productId}`);
    return response.data;
  },

  update: async (
    productId: string | number,
    overrides: ProductOverrideInput[]
  ): Promise<ProductOverridesState> => {
    const response = await axiosInstance.put<ProductOverridesState>(`${BASE}/products/${productId}`, { overrides });
    return response.data;
  },
};
